import { IconRefresh } from "@tabler/icons-react";
import LayoutHR from "layouts/layout-hr";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getRequest } from "services/api";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(Number(amount || 0));

const sumRows = (rows) => rows.reduce((total, row) => total + Number(row.amount || 0), 0);

const FinanceReportPage = () => {
  const [activeTab, setActiveTab] = useState("balance");
  const [loading, setLoading] = useState(true);
  const [financeData, setFinanceData] = useState(null);
  const [assetData, setAssetData] = useState(null);
  const [salaryData, setSalaryData] = useState([]);
  const [filters, setFilters] = useState(() => {
    const now = new Date();
    return {
      month: now.getMonth() + 1,
      year: now.getFullYear(),
    };
  });

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const queryString = `?month=${filters.month}&year=${filters.year}`;
      const [financeResponse, assetResponse, salaryResponse] = await Promise.allSettled([
        getRequest(`/ahr/report/finance${queryString}`),
        getRequest("/ahr/report/asset"),
        getRequest(`/ahr/report/salary${queryString}`),
      ]);

      if (financeResponse.status === "fulfilled" && financeResponse.value?.success) {
        setFinanceData(financeResponse.value.data);
      }
      if (assetResponse.status === "fulfilled" && assetResponse.value?.success) {
        setAssetData(assetResponse.value.data);
      }
      if (salaryResponse.status === "fulfilled" && salaryResponse.value?.success) {
        setSalaryData(salaryResponse.value.data || []);
      }
    } finally {
      setLoading(false);
    }
  }, [filters.month, filters.year]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const salaryExpense = useMemo(() => (
    salaryData || []
  ).reduce((total, item) => total + Number(item.total_salary || 0), 0), [salaryData]);

  const balanceRows = useMemo(() => {
    const assets = financeData?.balance_sheet?.assets || [
      { label: "Asset Kuber", amount: assetData?.summary?.total_value || 0 },
      { label: "Kas / Bank", amount: financeData?.cash_balance || 0 },
      { label: "Piutang", amount: financeData?.receivables || 0 },
    ];
    const liabilities = financeData?.balance_sheet?.liabilities || [
      { label: "Hutang", amount: financeData?.payables || 0 },
    ];
    const equity = financeData?.balance_sheet?.equity || [
      { label: "Modal / Saldo Berjalan", amount: Math.max(0, sumRows(assets) - sumRows(liabilities)) },
    ];

    return { assets, liabilities, equity };
  }, [assetData, financeData]);

  const profitLossRows = useMemo(() => {
    const income = financeData?.profit_loss?.income || [
      { label: "Pendapatan Operasional", amount: financeData?.operational_income || 0 },
      { label: "Pendapatan Lainnya", amount: financeData?.other_income || 0 },
    ];
    const expenses = financeData?.profit_loss?.expenses || [
      { label: "Beban Gaji", amount: salaryExpense },
      { label: "Beban Penyusutan Asset", amount: financeData?.depreciation_expense || 0 },
      { label: "Beban Operasional Lainnya", amount: financeData?.operational_expense || 0 },
    ];

    return { income, expenses };
  }, [financeData, salaryExpense]);

  const totalAssets = sumRows(balanceRows.assets);
  const totalLiabilities = sumRows(balanceRows.liabilities);
  const totalEquity = sumRows(balanceRows.equity);
  const totalIncome = sumRows(profitLossRows.income);
  const totalExpenses = sumRows(profitLossRows.expenses);
  const netProfit = totalIncome - totalExpenses;
  const balanceDifference = totalAssets - (totalLiabilities + totalEquity);
  const hasFinanceEndpointData = Boolean(financeData);

  return (
    <LayoutHR>
      <div className="container-fluid">
        <div className="row mb-3 mt-2">
          <div className="col-12">
            <div className="card">
              <div className="card-body py-3">
                <div className="row align-items-center g-3">
                  <div className="col-md-6">
                    <h3 className="card-title mb-1">Finance Report</h3>
                    <div className="text-muted small">
                      Neraca dan laba rugi periode {new Date(filters.year, filters.month - 1).toLocaleDateString("id-ID", { month: "long", year: "numeric" })}
                    </div>
                    <div className="mt-2">
                      <span className={`badge ${hasFinanceEndpointData ? "bg-success-lt text-success" : "bg-warning-lt text-warning"}`}>
                        {hasFinanceEndpointData ? "Finance API connected" : "Fallback dari asset & salary report"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex gap-2 justify-content-end">
                      <select
                        className="form-select form-select-sm"
                        value={filters.month}
                        onChange={(event) => setFilters((current) => ({ ...current, month: Number(event.target.value) }))}
                        style={{ width: 150 }}
                      >
                        {Array.from({ length: 12 }, (_, index) => (
                          <option key={index + 1} value={index + 1}>
                            {new Date(0, index).toLocaleDateString("id-ID", { month: "long" })}
                          </option>
                        ))}
                      </select>
                      <select
                        className="form-select form-select-sm"
                        value={filters.year}
                        onChange={(event) => setFilters((current) => ({ ...current, year: Number(event.target.value) }))}
                        style={{ width: 110 }}
                      >
                        {Array.from({ length: 10 }, (_, index) => filters.year - 5 + index).map((year) => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                      <button className="btn btn-outline-primary btn-sm" onClick={fetchReports} disabled={loading}>
                        {loading ? <span className="spinner-border spinner-border-sm" /> : <IconRefresh size={16} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-3 mb-3">
          <SummaryCard label="Total Asset" value={formatCurrency(totalAssets)} tone="primary" />
          <SummaryCard label="Liabilitas" value={formatCurrency(totalLiabilities)} tone="danger" />
          <SummaryCard label="Ekuitas" value={formatCurrency(totalEquity)} tone="success" />
          <SummaryCard label="Laba / Rugi Bersih" value={formatCurrency(netProfit)} tone={netProfit >= 0 ? "success" : "danger"} />
        </div>

        <div className="card">
          <div className="card-header p-0 d-flex align-items-center justify-content-between">
            <div className="nav nav-tabs card-header-tabs">
              <button className={`nav-link ${activeTab === "balance" ? "active" : ""}`} onClick={() => setActiveTab("balance")}>
                Neraca
              </button>
              <button className={`nav-link ${activeTab === "profit-loss" ? "active" : ""}`} onClick={() => setActiveTab("profit-loss")}>
                Laba Rugi
              </button>
            </div>
            {activeTab === "balance" && (
              <div className="pe-3">
                <span className={`badge ${Math.abs(balanceDifference) < 1 ? "bg-success-lt text-success" : "bg-warning-lt text-warning"}`}>
                  Selisih Neraca {formatCurrency(balanceDifference)}
                </span>
              </div>
            )}
          </div>
          <div className="card-body">
            {activeTab === "balance" ? (
              <div className="row g-3">
                <div className="col-lg-7">
                  <ReportSection title="Asset" rows={balanceRows.assets} total={totalAssets} tone="primary" />
                </div>
                <div className="col-lg-5">
                  <div className="d-flex flex-column gap-3">
                    <ReportSection title="Liabilitas" rows={balanceRows.liabilities} total={totalLiabilities} tone="danger" />
                    <ReportSection title="Ekuitas" rows={balanceRows.equity} total={totalEquity} tone="success" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="row g-3">
                <div className="col-lg-6">
                  <ReportSection title="Pendapatan" rows={profitLossRows.income} total={totalIncome} tone="success" />
                </div>
                <div className="col-lg-6">
                  <ReportSection title="Beban" rows={profitLossRows.expenses} total={totalExpenses} tone="danger" />
                </div>
                <div className="col-12">
                  <div className={`card border-0 ${netProfit >= 0 ? "bg-success-lt" : "bg-danger-lt"}`}>
                    <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-2">
                      <div>
                        <div className="text-muted small">Laba / Rugi Bersih</div>
                        <div className="small text-muted mt-1">Pendapatan dikurangi total beban periode berjalan.</div>
                      </div>
                      <div className="h2 m-0">{formatCurrency(netProfit)}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </LayoutHR>
  );
};

const SummaryCard = ({ label, value, tone }) => (
  <div className="col-sm-6 col-xl-3">
    <div className={`card bg-${tone}-lt border-0 h-100`}>
      <div className="card-body">
        <div className="text-muted small">{label}</div>
        <div className="h3 m-0">{value}</div>
      </div>
    </div>
  </div>
);

const ReportSection = ({ title, rows, total, tone = "primary" }) => (
  <div className="card h-100">
    <div className="card-header py-2">
      <h3 className="card-title mb-0">{title}</h3>
    </div>
    <div className="table-responsive">
      <table className="table table-vcenter mb-0">
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.label}-${index}`}>
              <td>{row.label}</td>
              <td className="text-end fw-bold">{formatCurrency(row.amount)}</td>
            </tr>
          ))}
          <tr className={`bg-${tone}-lt`}>
            <td className="fw-bold">Total {title}</td>
            <td className="text-end fw-bold">{formatCurrency(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

export default FinanceReportPage;
